import {t} from "i18next";
import PageHeader from "../../components/PageHeader/PageHeader";
import PageContainer from "../../components/PageContainer/PageContainer";
import DataTable from "../../components/DataTable/DataTable";
import useFetchDepartment from "../../hooks/useFetchDepartments";
import Loading from "../../components/Loading/Loading";
import {useCallback, useEffect, useState} from "react";
import AlertDanger from "../../components/Alerts/AlertDanger";
import PlusIcon from "../../assets/svg/buttonIcons/PlusIcon";
import MainButton from "../../components/buttons/MainButton";
import TableEditIcon from "../../assets/svg/TableEditIcon";
import DeleteItemIcon from "../../assets/svg/DeleteItemIcon";
import {CreateDepartmentDto, DepartmentDto} from "../../dtos/departments/DepartmentDto";
import useFetchData from "../../hooks/useFetchData";
import QuestionModal from "../../components/QuestionModal/QuestionModal";
import {EditTaskSourceModal} from "./modals/EditTaskSourceModal";
import {TaskSourceDto} from "../../dtos/taskSource/TaskSourceDto";
import SearchInput from "../../components/inputs/SearchInput.tsx";

const TaskSourcesPage = () => {
    const {
        items: taskSources,
        error,
        deleteItem,
        fetchItems,
        isLoading,
        totalCount,
    } = useFetchData<CreateDepartmentDto, DepartmentDto, DepartmentDto>("EmployeeTaskSources")

    const [selectedTaskSourceId, setSelectedTaskSourceId] = useState<string | undefined>(undefined);
    const [showTaskSourceDeleteQuestionModal, setShowTaskSourceDeleteQuestionModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchText, setSearchText] = useState<string>("");

    const columns: DataTableColumn[] = [
        {
            title: t("TaskSourceName"),
            field: "description"
        },
        {
            title: t("Code"),
            field: "id"
        },
        {
            title: t("Actions"),
            field: "actions",
            render: (value: any) => (
                <div className="flex gap-2">
                    <button onClick={() => handleEditTaskSource(value)}><TableEditIcon/></button>
                    <button onClick={() => handleDeleteTaskSource(value)}><DeleteItemIcon/></button>
                </div>
            )
        }
    ]

    const handleOnReload = (newPageSize, newPageNumber) => {
        setPageSize(newPageSize);
        setPageNumber(newPageNumber);
    };
    const handleCloseModal = async () => {
        setShowEditModal(false);
    }

    const handleTaskSourceUpdate = async () => {
        setShowEditModal(false);
        await fetchData();
    };

    const handleNewTaskSource = () => {
        setSelectedTaskSourceId(undefined);
        setShowEditModal(true);
    };

    const handleEditTaskSource = (department: DepartmentDto) => {
        setSelectedTaskSourceId(department.id);
        setShowEditModal(true);
    };

    const handleDeleteTaskSource = async (taskSource: TaskSourceDto) => {
        setSelectedTaskSourceId(taskSource.id);
        setShowTaskSourceDeleteQuestionModal(true);
    };

    const handleDeleteTaskSourceConfirmed = async () => {
        setShowTaskSourceDeleteQuestionModal(false);
        if (!selectedTaskSourceId) return;

        await deleteItem(selectedTaskSourceId);
        await fetchData();
    };

    const fetchData = useCallback(async () => {
        await fetchItems(searchText, (pageNumber - 1) * pageSize, pageSize);
    }, [pageSize, pageNumber, searchText]);

    useEffect(() => {
        fetchData();
    }, [pageSize, pageNumber, searchText])

    return (
        <div>
            <PageHeader title={t("TaskSources")}>
                <div className={"flex gap-2"}>
                    <SearchInput searchText={searchText} onSearchTextChanged={setSearchText}/>
                    <MainButton
                        disabled={isLoading}
                        title={t("NewSource")}
                        onClick={handleNewTaskSource}
                        icon={<PlusIcon/>}
                    />
                </div>
            </PageHeader>
            {error && <AlertDanger errorMessages={[error]}/>}
            <PageContainer>
                <Loading loading={isLoading}>
                    <div className={"mt-2"}>
                        <DataTable
                            items={taskSources}
                            columns={columns}
                            pageSize={pageSize}
                            currentPage={pageNumber}
                            totalCount={totalCount}
                            onReload={handleOnReload}
                        />
                    </div>
                </Loading>
            </PageContainer>
            {showEditModal && (
                <EditTaskSourceModal
                    onClose={handleCloseModal}
                    onChanged={handleTaskSourceUpdate}
                    taskSourceId={selectedTaskSourceId}
                />
            )}

            {showTaskSourceDeleteQuestionModal && (
                <QuestionModal onClose={() => setShowTaskSourceDeleteQuestionModal(false)}
                               onYes={handleDeleteTaskSourceConfirmed}
                               title={t("AreYouSureYouWantToDeleteThisSource")}
                               content={t("ThisActionCannotBeUndone")}
                               icon={<DeleteItemIcon/>} type={"danger"}
                               yesButtonText={t("Yes")}
                               noButtonText={t("No")}/>
            )}
        </div>
    );
};

export default TaskSourcesPage;